import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, CalendarDays, Mail, UserPlus, Plus, ArrowUpRight, QrCode,
  Check, HelpCircle, X, Clock, MailOpen, MousePointerClick, MailWarning,
  Send, UsersRound,
} from 'lucide-react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from 'recharts';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { QrShareCard } from '../components/events/QrShareCard';
import { FollowUps } from '../components/dashboard/FollowUps';

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]">
    <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${tone === 'primary' ? 'bg-primary/10' : tone === 'accent' ? 'bg-accent/10' : 'bg-secondary'}`}>
      <Icon className={`h-5 w-5 ${tone === 'primary' ? 'text-primary' : tone === 'accent' ? 'text-accent' : 'text-foreground'}`} />
    </div>
    <div className="font-display text-3xl font-semibold">{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const RsvpTile = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
      <Icon className="h-3.5 w-3.5" style={{ color }} /> {label}
    </div>
    <div className="font-display text-2xl font-semibold">{value}</div>
  </div>
);

const RateRow = ({ icon: Icon, label, count, rate, showRate = true }) => (
  <div className="flex items-center gap-3">
    <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between text-sm">
        <span className="text-foreground">{label}</span>
        <span className="font-medium tabular-nums">{count}{showRate ? <span className="text-muted-foreground font-normal"> · {rate}%</span> : ''}</span>
      </div>
      {showRate && <Progress value={rate} className="h-1.5 mt-1.5" />}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all');
  const [scopeLoading, setScopeLoading] = useState(false);
  const [shareEventId, setShareEventId] = useState('');

  useEffect(() => {
    // Fetch independently so one failing call never blanks the whole dashboard.
    let active = true;
    Promise.allSettled([api.get('/stats'), api.get('/events'), api.get('/analytics/dashboard')])
      .then(([s, e, a]) => {
        if (!active) return;
        if (s.status === 'fulfilled') setStats(s.value.data);
        if (e.status === 'fulfilled') {
          setEvents(e.value.data || []);
          if (e.value.data?.length) setShareEventId(e.value.data[0].id);
        }
        if (a.status === 'fulfilled') setAnalytics(a.value.data);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const changeScope = useCallback(async (val) => {
    setScope(val);
    setScopeLoading(true);
    try {
      const { data } = await api.get('/analytics/dashboard', {
        params: val === 'all' ? {} : { event_id: val },
      });
      setAnalytics(data);
    } catch (e) { /* noop */ } finally { setScopeLoading(false); }
  }, []);

  const shareEvent = events.find((e) => e.id === shareEventId) || events[0];
  const rsvp = analytics?.rsvp || {};
  const totals = analytics?.totals || {};
  const perEvent = analytics?.per_event || [];

  const rsvpData = [
    { name: 'Going', value: rsvp.going || 0, color: 'hsl(var(--primary))' },
    { name: 'Maybe', value: rsvp.maybe || 0, color: 'hsl(var(--accent))' },
    { name: 'Declined', value: rsvp.declined || 0, color: 'hsl(var(--destructive))' },
    { name: 'No reply', value: rsvp.no_reply || 0, color: 'hsl(var(--muted-foreground))' },
  ];
  const hasResponses = rsvpData.some((d) => d.value > 0);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Hello, ${user?.name?.split(' ')[0] || 'there'} 👋`} subtitle="Here's what's happening with your gatherings.">
        <Button onClick={() => navigate('/app/events/new')} className="gap-2" data-testid="dashboard-create-event-button">
          <Plus className="h-4 w-4" /> Create Event
        </Button>
      </PageHeader>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Contacts" value={stats?.contacts || 0} tone="primary" />
            <StatCard icon={CalendarDays} label="Events" value={stats?.events || 0} tone="accent" />
            <StatCard icon={Mail} label="Invites sent" value={stats?.delivered || 0} tone="primary" />
            <StatCard icon={UserPlus} label="Email signups" value={stats?.signups || 0} tone="accent" />
          </div>

          {/* Analytics header + scope filter */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-8 mb-4">
            <div>
              <h2 className="font-display text-xl font-semibold">Invitations &amp; responses</h2>
              <p className="text-sm text-muted-foreground">How your invites are landing and who&apos;s coming.</p>
            </div>
            <div className="sm:w-64">
              <Select value={scope} onValueChange={changeScope}>
                <SelectTrigger data-testid="analytics-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 transition-opacity ${scopeLoading ? 'opacity-50' : ''}`}>
            {/* RSVP responses */}
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="rsvp-breakdown-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold">RSVP responses</h3>
                <Badge variant="secondary" className="gap-1.5"><UsersRound className="h-3.5 w-3.5 text-primary" /> {rsvp.headcount || 0} attending</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <RsvpTile icon={Check} label="Going" value={rsvp.going || 0} color="hsl(var(--primary))" testid="rsvp-going" />
                <RsvpTile icon={HelpCircle} label="Maybe" value={rsvp.maybe || 0} color="hsl(var(--accent))" testid="rsvp-maybe" />
                <RsvpTile icon={X} label="Declined" value={rsvp.declined || 0} color="hsl(var(--destructive))" testid="rsvp-declined" />
                <RsvpTile icon={Clock} label="No reply" value={rsvp.no_reply || 0} color="hsl(var(--muted-foreground))" testid="rsvp-noreply" />
              </div>
              <div className="mt-5">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground">Response rate</span>
                  <span className="font-medium" data-testid="response-rate">{rsvp.response_rate || 0}%</span>
                </div>
                <Progress value={rsvp.response_rate || 0} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {rsvp.responded || 0} of {totals.invited || 0} invited guests have responded.
                </p>
              </div>
            </div>

            {/* Email reactions */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="email-reactions-card">
              <h3 className="font-display text-lg font-semibold mb-4">Email reactions</h3>
              {totals.sent ? (
                <div className="space-y-4">
                  <RateRow icon={Mail} label="Delivered" count={totals.delivered || 0} rate={totals.delivered_rate || 0} />
                  <RateRow icon={MailOpen} label="Opened" count={totals.opened || 0} rate={totals.open_rate || 0} />
                  <RateRow icon={MousePointerClick} label="Clicked" count={totals.clicked || 0} rate={totals.click_rate || 0} />
                  <RateRow icon={MailWarning} label="Bounced" count={totals.bounced || 0} rate={totals.bounce_rate || 0} showRate={false} />
                  <p className="text-xs text-muted-foreground pt-1">Based on {totals.sent} invite email{totals.sent !== 1 ? 's' : ''} sent.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-10">
                  <Send className="h-8 w-8 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">No invites sent yet. Open analytics here once your invitations go out.</p>
                </div>
              )}
            </div>
          </div>

          {/* Response breakdown chart */}
          {hasResponses && (
            <div className="rounded-2xl border border-border bg-card p-6 mt-4 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="rsvp-chart-card">
              <h3 className="font-display text-lg font-semibold mb-1">Response breakdown</h3>
              <p className="text-sm text-muted-foreground mb-4">{scope === 'all' ? 'Across all your events' : 'For the selected event'}</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rsvpData} barCategoryGap={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'hsl(var(--secondary))' }} contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))' }} />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {rsvpData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Per-event comparison */}
          {perEvent.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-6 mt-4 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="per-event-table">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold">Event performance</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/app/events')} className="gap-1 text-primary">
                  All events <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="font-medium px-2 py-2">Event</th>
                      <th className="font-medium px-2 py-2 text-center">Invited</th>
                      <th className="font-medium px-2 py-2 text-center text-primary">Going</th>
                      <th className="font-medium px-2 py-2 text-center text-accent">Maybe</th>
                      <th className="font-medium px-2 py-2 text-center">No reply</th>
                      <th className="font-medium px-2 py-2 text-center">Open rate</th>
                      <th className="font-medium px-2 py-2 text-center">Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perEvent.map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => navigate(`/app/events/${e.id}`)}
                        className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors"
                        data-testid="per-event-row"
                      >
                        <td className="px-2 py-3">
                          <div className="font-medium text-foreground truncate max-w-[200px]">{e.title || 'Untitled'}</div>
                          <div className="text-xs text-muted-foreground">{e.date || 'No date'}</div>
                        </td>
                        <td className="px-2 py-3 text-center tabular-nums">{e.invited}</td>
                        <td className="px-2 py-3 text-center tabular-nums font-medium text-primary">{e.going}</td>
                        <td className="px-2 py-3 text-center tabular-nums text-accent">{e.maybe}</td>
                        <td className="px-2 py-3 text-center tabular-nums text-muted-foreground">{e.no_reply}</td>
                        <td className="px-2 py-3 text-center tabular-nums">{e.open_rate}%</td>
                        <td className="px-2 py-3 text-center tabular-nums">{e.response_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Follow-ups + Quick share */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <FollowUps />
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-semibold">Upcoming events</h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/app/events')} className="gap-1 text-primary">
                    All <ArrowUpRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {events.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events yet. Create your first one!</p>
                ) : (
                  <div className="space-y-3">
                    {events.slice(0, 4).map((ev) => (
                      <button key={ev.id} onClick={() => navigate(`/app/events/${ev.id}`)}
                        className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-colors" data-testid="dashboard-event-item">
                        <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: (ev.theme_color || '#2BB7C4') + '20' }}>
                          <CalendarDays className="h-5 w-5" style={{ color: ev.theme_color || '#2BB7C4' }} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{ev.title}</div>
                          <div className="text-xs text-muted-foreground">{ev.date || 'No date'} · {ev.invites_sent} invited</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {events.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-6 mt-4 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]" data-testid="dashboard-quick-share">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <QrCode className="h-[18px] w-[18px] text-primary" /> Quick share
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Show or save QR codes to share an event on the go.</p>
                </div>
                <div className="sm:w-64">
                  <Select value={shareEventId} onValueChange={setShareEventId}>
                    <SelectTrigger data-testid="quick-share-event-select"><SelectValue placeholder="Choose an event" /></SelectTrigger>
                    <SelectContent>
                      {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <QrShareCard event={shareEvent} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
